import { z } from "zod";

export const ALL_COUNTRIES = [
  "VN",
  "PH",
  "US",
  "LK",
  "IN",
  "ID",
  "MX",
  "ES",
  "CO",
  "PE",
  "MY",
  "BR",
] as const;

export const CountrySchema = z.enum(ALL_COUNTRIES);

export type Country = z.infer<typeof CountrySchema>;

export const CURRENTLY_OPERATING_COUNTRIES = [
  "VN",
  "PH",
  "MX",
  "ID",
  "ES",
  "CO",
  "IN",
  "PE",
  "MY",
  "BR",
] as const;

export const CurrentlyOperatingCountrySchema = z.enum(
  CURRENTLY_OPERATING_COUNTRIES,
);

export type CurrentlyOperatingCountry = z.infer<
  typeof CurrentlyOperatingCountrySchema
>;

export const isCurrentlyOperating = ({
  country,
}: {
  country: string;
}): boolean => {
  return (CURRENTLY_OPERATING_COUNTRIES as readonly string[]).includes(country);
};

export const isCurrentlyOperatingCountry = (
  country: string,
): country is CurrentlyOperatingCountry =>
  (CURRENTLY_OPERATING_COUNTRIES as readonly string[]).includes(country);

export type PhonePrefix = {
  prefix: string;
  inputPrefix: string;
};

export type PhonePrefixes = {
  [key in Country]: PhonePrefix;
};

export const ALL_PHONE_PREFIXES: PhonePrefixes = {
  VN: {
    prefix: "+84",
    inputPrefix: "+84(0)",
  },
  US: {
    prefix: "+1",
    inputPrefix: "+1(0)",
  },
  PH: {
    prefix: "+63",
    inputPrefix: "09",
  },
  IN: {
    prefix: "+91",
    inputPrefix: "+91(0)",
  },
  ID: {
    prefix: "+62",
    inputPrefix: "0",
  },
  LK: {
    prefix: "+94",
    inputPrefix: "+94",
  },
  MX: {
    prefix: "+52",
    inputPrefix: "+52",
  },
  ES: {
    prefix: "+34",
    inputPrefix: "+34",
  },
  CO: {
    prefix: "+57",
    inputPrefix: "+57",
  },
  PE: {
    prefix: "+51",
    inputPrefix: "+51",
  },
  MY: {
    prefix: "+60",
    inputPrefix: "+60",
  },
  BR: {
    prefix: "+55",
    inputPrefix: "+55",
  },
};

export type CountryHub = "asia" | "latam";

const COUNTRY_LIST: Record<
  CurrentlyOperatingCountry,
  { slug: string; name: string; hub: CountryHub }
> = {
  VN: { slug: "vietnam", name: "Vietnam", hub: "asia" },
  PH: { slug: "philippines", name: "Philippines", hub: "asia" },
  MX: { slug: "mexico", name: "Mexico", hub: "latam" },
  ID: { slug: "indonesia", name: "Indonesia", hub: "asia" },
  ES: { slug: "spain", name: "Spain", hub: "latam" },
  CO: { slug: "colombia", name: "Colombia", hub: "latam" },
  IN: { slug: "india", name: "India", hub: "asia" },
  PE: { slug: "peru", name: "Peru", hub: "latam" },
  MY: { slug: "malaysia", name: "Malaysia", hub: "asia" },
  BR: { slug: "brazil", name: "Brazil", hub: "latam" },
};

export const getCountryBySlug = (slug: string): CurrentlyOperatingCountry => {
  const country = Object.entries(COUNTRY_LIST).find(
    ([_, value]) => value.slug === slug,
  )?.[0] as CurrentlyOperatingCountry;
  if (!country) {
    throw new Error(`Country not found for slug: ${slug}`);
  }
  return country;
};

export const getCountrySlug = (country: CurrentlyOperatingCountry): string => {
  const item = COUNTRY_LIST[country];
  if (!item) {
    throw new Error(`Country slug not found for country: ${country}`);
  }
  return item.slug;
};

export const getCountryName = (country: CurrentlyOperatingCountry): string => {
  const item = COUNTRY_LIST[country];
  if (!item) {
    throw new Error(`Country name not found for country: ${country}`);
  }
  return item.name;
};

export const getCountryFlag = (code: string): string =>
  code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join("");

export const getCountryHub = (country: CurrentlyOperatingCountry): CountryHub =>
  COUNTRY_LIST[country].hub;
